import React, { useRef, useState } from 'react';
import { useSpring, animated } from 'react-spring';
import PropTypes from 'prop-types';
import NavItem from './NavItem';
import Logo from '../../_shared/components/Logo';
import NavSearchBar from '../NavSearchBar';
import SearchModal from '../SearchModal';
import Notifications from '../Notifications';
import DealerProfile from '../DealerProfile';
import MixpanelService from '../../services/MixpanelService';
import MixpanelConfig from '../../_shared/constants/mixpanelConfig';

import {
  StyledNav,
  StyledDealerProfileContainer,
  StyledDealerName,
  StyledDropdownIcon,
  ReactTooltipStyled
} from './Styles';
import iconMap from '../../_shared/constants/iconMap';
import renderLocale from '../../_shared/helpers/renderLocale';
import messages from '../../assets/messages';
import useBadgeList from '../../_shared/hooks/useBadgeList';

// TODO: Move to a different file
const NAV_TYPE = {
  PRIMARY: 'Primary',
  SECONDARY: 'Secondary'
};

const propTypes = {
  testId: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
  position: PropTypes.shape({
    left: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  variant: PropTypes.oneOf([NAV_TYPE.PRIMARY, NAV_TYPE.SECONDARY])
};

const defaultProps = {
  testId: undefined,
  className: undefined,
  children: undefined,
  position: {
    left: 0,
    width: 0
  },
  variant: NAV_TYPE.PRIMARY
};

const Nav = ({ className, children, testId, position, user, variant }) => {
  const mixpanel = MixpanelService.get();
  const animationProps = useSpring({
    from: {
      opacity: 0
    },
    left: position.left,
    width: position.width,
    opacity: 1
  });

  const [isSearchModalOpen, setSearchModalOpen] = useState(false);
  const [isDealerProfileOpen, setIsDealerProfileOpen] = useState(false);

  const ref = useRef();

  const toggleDealerProfile = () => {
    if (!isDealerProfileOpen)
      mixpanel.track(MixpanelConfig.names.dealerProfileMenu, {
        dealer_option: 'dealer info screen'
      });
    setIsDealerProfileOpen(isOpen => !isOpen);
  };

  const locale = renderLocale();

  const badges = useBadgeList(user?.dealerId);

  return (
    <StyledNav className={className} data-testid={testId} variant={variant}>
      {variant === NAV_TYPE.PRIMARY && <Logo />}
      {variant === NAV_TYPE.SECONDARY && (
        <>
          <NavSearchBar
            testId={testId}
            isModalOpen={isSearchModalOpen}
            openModalHandler={() => setSearchModalOpen(true)}
          />
          {isSearchModalOpen && (
            <SearchModal
              testId={testId}
              isModalOpen={isSearchModalOpen}
              closeHandler={() => setSearchModalOpen(false)}
            />
          )}
        </>
      )}
      <ul data-testid={`${testId}-list-wrapper`}>{children}</ul>
      <animated.div
        className="active-indicator"
        data-testid="active-indicator"
        style={animationProps}
      />

      {variant === NAV_TYPE.PRIMARY && user && <Notifications />}
      {variant === NAV_TYPE.PRIMARY && user && (
        <StyledDealerProfileContainer>
          <StyledDealerName onClick={() => toggleDealerProfile()}>
            <StyledDealerName.UserName className="notranslate">
              {user?.fullName}
            </StyledDealerName.UserName>
            {!badges || badges?.length === 0 ? (
              <StyledDealerName.CompanyName className="notranslate">
                {user?.companyName}
              </StyledDealerName.CompanyName>
            ) : (
              <>
                <StyledDealerName.FloatRight>
                  <StyledDealerName.CompanyBadgeContainer
                    data-tip
                    data-for="companyBadgeTooltip"
                  >
                    <StyledDealerName.CompanyBadge
                      src={iconMap[badges[0].id]}
                    />
                    <StyledDealerName.CompanyNameWithBadge className="notranslate">
                      {user?.companyName}
                    </StyledDealerName.CompanyNameWithBadge>
                  </StyledDealerName.CompanyBadgeContainer>
                </StyledDealerName.FloatRight>
                <ReactTooltipStyled
                  id="companyBadgeTooltip"
                  arrowColor="transparent"
                  place="bottom"
                >
                  <span>
                    {
                      messages[locale][
                        `dealerBades.nav.tooltip.${badges[0].id}`
                      ]
                    }
                  </span>
                </ReactTooltipStyled>
              </>
            )}
          </StyledDealerName>
          <StyledDropdownIcon clickHandler={() => toggleDealerProfile()} />
          <DealerProfile
            ref={ref}
            username={user?.fullName}
            isDealerProfileOpen={isDealerProfileOpen}
            setIsDealerProfileOpen={setIsDealerProfileOpen}
          />
        </StyledDealerProfileContainer>
      )}
    </StyledNav>
  );
};

Nav.propTypes = propTypes;
Nav.defaultProps = defaultProps;

Nav.Item = NavItem;

export default Nav;
